import { IconButton, List, ListItem, ListItemAvatar, Menu, MenuItem, Tooltip } from '@mui/material';
import { useLogoutMutation } from 'api/auth';
import Avatar from 'components/@extended/Avatar';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {  RootState } from 'store';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { resetDocumentElements } from 'store/slice/design/documentElementsSlice';
import { setLocalStorageItems } from 'utils/fileUtils';
import { Typography } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTheme } from '@mui/material';
import useConfig from 'hooks/useConfig';
import { ThemeMode } from 'types/config';
import { showPageBlocker } from 'store/reducers/pageBlockerSlice';
const translate = (key: string, options?: any) => (window as any)?.translate?.(key, options);

export const DrawerFooter = () => {
  const dispatch = useDispatch();
  const [logout, { isLoading: logoutInProgress }] = useLogoutMutation();
  const [open, setOpen] = useState(false);
  const avatarRef = useRef(null);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const theme = useTheme();
  const { onChangeMode } = useConfig();

  const decodedAuthToken = useSelector((state: RootState) => state.auth.decodedAuthToken);
  const isLDAPUserORExternaluser = useMemo(
    () => decodedAuthToken?.IsLDAP?.toLowerCase() === 'true' || decodedAuthToken?.IsExternalUser?.toLowerCase() === 'true',
    [decodedAuthToken]
  );

  const isFormDirty = useSelector((state: RootState) => state.formslice.isDirty);

  const navigate = useNavigate();

  const onClose = useCallback(() => setOpen(false), []);
  const onHover = useCallback((event: any) => {
    avatarRef.current = event.currentTarget;
    setOpen(true);
  }, []);
  const location = useLocation();
  const onLogoutConfirm = useCallback(async () => {
    try {
      if (decodedAuthToken) {
        setLocalStorageItems({
          user_id: decodedAuthToken.nameid,
          last_location: location.pathname
        });
      }
      await logout().unwrap();
      const lastCloseTime = localStorage.getItem('browserCloseTime');
      if (lastCloseTime) {
        localStorage.removeItem('browserCloseTime');
      }
      dispatch(resetDocumentElements());
      setTimeout(() => {
        dispatch(
          openDefaultSuccessSnackbar({
            message: 'Your session is logged out successfully',
            preventAutoDismiss: true,
            hideDuration: 15000
          })
        );
      }, 1500);
    } catch (e) {}
  }, [dispatch, logout, location.pathname]);

  const onLogout = useCallback(() => {
    setShowLogoutConfirmation(true);
  }, []);

  const handleChangePassword = useCallback(() => {
    navigate('/change-password');
  }, [navigate]);

  const checkLogoutProcess = useCallback(() => {
    setShowLogoutConfirmation(false);
    if (isFormDirty) {
      //setForcePageBlock(true);
      dispatch(
        showPageBlocker({
          onConfirm: () => {
            onLogoutConfirm();
          },
          onCancel: () => {}
        })
      );
    } else {
      onLogoutConfirm();
    }
  }, [isFormDirty, onLogoutConfirm]);

  const onToggleTheme = () => {
    if (theme.palette.mode === ThemeMode.LIGHT) {
      onChangeMode(ThemeMode.DARK);
      document.body.classList.remove('light', 'dark');
      document.body.classList.add('dark');
    } else {
      onChangeMode(ThemeMode.LIGHT);
      document.body.classList.remove('light', 'dark');
      document.body.classList.add('light');
    }
  };

  return (
    <>
      <List className="menubar-footer">
        <ListItem>
          <ListItemAvatar // disabled dark theme mode
            sx={{ display: 'none !important' }}
          >
            <Tooltip
              placement="right"
              title={
                <Typography>
                  {theme.palette.mode === ThemeMode.DARK ? translate('menu.theme.switchToLight') : translate('menu.theme.switchToDark')}
                </Typography>
              }
            >
              <IconButton size="large" onClick={onToggleTheme}>
                <LightModeIcon color={theme.palette.mode === ThemeMode.DARK ? 'primary' : 'secondary'} />
              </IconButton>
            </Tooltip>
          </ListItemAvatar>
        </ListItem>
        <ListItem>
          <ListItemAvatar>
            <Avatar ref={avatarRef} onMouseOver={onHover} />
            <Menu
              id="basic-menu"
              anchorEl={avatarRef.current}
              open={open}
              onClose={onClose}
              onClick={onClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {!isLDAPUserORExternaluser && <MenuItem onClick={handleChangePassword}>Change Password</MenuItem>}
              <MenuItem onClick={onLogout}>Logout</MenuItem>
            </Menu>
          </ListItemAvatar>
        </ListItem>
      </List>
      <ConfirmationDialog
        open={showLogoutConfirmation}
        onClose={() => setShowLogoutConfirmation(false)}
        onConfirm={checkLogoutProcess}
        isLoading={logoutInProgress}
        message="Logout Confirmation"
        additionalContent={
          <Typography my={2} className="popup-description">
            Are you sure you want to logout?
          </Typography>
        }
        titleIconType="warning"
      />
    </>
  );
};
